/**
 * authUtils.js
 * ------------
 * Helpers to safely read auth/role information from localStorage.
 * These are pure read-only utilities — they never write to storage.
 *
 * Used by AdminContext (Phase 2) to determine admin status and
 * retrieve the client list returned from the admin login response.
 */

/**
 * Safely parses credentials from localStorage.
 * Returns the parsed object or {} if missing/invalid.
 */
const getCredentials = () => {
    try {
        const raw = localStorage.getItem('credentials');
        if (!raw) return {};
        return JSON.parse(raw) || {};
    } catch {
        return {};
    }
};

/**
 * Returns the `role` string from stored credentials.
 * e.g. "admin" | "lab" | null
 */
export const getUserRole = () => {
    return getCredentials().role || null;
};

/**
 * Returns true if the logged-in user has the "admin" role.
 * For all regular client accounts this returns false — no side-effects.
 */
export const isAdmin = () => getUserRole() === 'admin';

/**
 * Returns the clientData array from the admin login response.
 * Each entry looks like: { clientId, userId, org, center, role, endpoints? }
 *
 * For regular client logins this returns [] (clientData is not in their response).
 */
export const getAdminClientData = () => {
    return getCredentials().clientData || [];
};
